import type { AdminApplicationDto } from '@getslot/api-client'
import type { ApplicationStatus } from '@getslot/domain'
import { ago } from './format'

/**
 * Группы очереди по тому, кто должен действовать. Сверху то, что ждёт оператора;
 * внутри группы старые выше (API отдаёт по времени смены статуса).
 */
export const LANES = [
  { key: 'operator', title: 'Требуют меня', statuses: ['needs_operator'], tone: 'operator' },
  { key: 'found', title: 'Дата найдена, проверьте', statuses: ['slot_found', 'booking'], tone: 'found' },
  { key: 'client', title: 'Ждём клиента', statuses: ['needs_client', 'awaiting_payment'], tone: 'client' },
  { key: 'search', title: 'Ищем', statuses: ['searching'], tone: 'search' },
  { key: 'new', title: 'Новые', statuses: ['created', 'access_verified'], tone: 'fresh' },
  { key: 'booked', title: 'Записаны', statuses: ['booked'], tone: 'booked' },
  { key: 'paused', title: 'Пауза', statuses: ['paused'], tone: 'pause' },
  { key: 'closed', title: 'Завершены и остановлены', statuses: ['completed', 'stopped'], tone: 'stopped' },
] as const satisfies readonly { key: string; title: string; statuses: readonly ApplicationStatus[]; tone: string }[]

export type LaneKey = (typeof LANES)[number]['key']

export function parseLane(value: unknown): LaneKey | undefined {
  return LANES.find((l) => l.key === value)?.key
}

export function laneOf(status: ApplicationStatus): LaneKey {
  return LANES.find((l) => (l.statuses as readonly ApplicationStatus[]).includes(status))?.key ?? 'closed'
}

/** Чего ждём сейчас: причина ветки, если есть, иначе подсказка по статусу. */
export function waitingFor(a: AdminApplicationDto): string {
  if (a.status_reason !== null) return a.status_reason
  switch (a.status) {
    case 'created':
      return 'Проверяем вход в кабинет'
    case 'access_verified':
      return 'Вход проверен, запускаем поиск'
    case 'searching':
      return `Последняя проверка ${ago(a.last_polled_at)}`
    case 'slot_found':
      return 'Watcher нашёл дату, сверьте с окном клиента'
    case 'booking':
      return 'Идёт запись, ждём подтверждения из кабинета'
    case 'booked':
      return 'Записан, подтверждено в кабинете'
    case 'awaiting_payment':
      return 'Ждём оплату'
    default:
      return ''
  }
}

/** Главная кнопка строки очереди. */
export function primaryAction(status: ApplicationStatus): { label: string; variant: 'cta' | 'ink' | 'secondary' } {
  if (status === 'needs_operator') return { label: 'Разобрать', variant: 'cta' }
  if (status === 'slot_found' || status === 'booking') return { label: 'Проверить', variant: 'ink' }
  return { label: 'Открыть', variant: 'secondary' }
}
